import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import Typography from '../Text/Typography';
import Button from '../Button/Button';
import Icon from '../Icon/Icon';
import './ModalDialog.css';

interface ModalDialogProps {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    children: React.ReactNode;
    primaryButton?: {
        label: string;
        onClick: () => void;
        disabled?: boolean;
    };
    secondaryButton?: {
        label: string;
        onClick: () => void;
        disabled?: boolean;
    };
    maxWidth?: string;
    maxHeight?: string;
}

const ModalDialog = ({ 
    isOpen, 
    onClose, 
    title, 
    children, 
    primaryButton, 
    secondaryButton,
    maxWidth,
    maxHeight
}: ModalDialogProps) => {
    useEffect(() => {
      if (!isOpen) return;
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') onClose();
      };
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    return createPortal(
      <div className="modal-overlay" onClick={onClose}>
        <div 
          className="modal-content" 
          onClick={(e) => e.stopPropagation()}
          style={{ maxWidth, maxHeight }}
        >
          {/* Header */}
          <div className="modal-header">
            <Typography variant="h3" className="modal-title">
              {title}
            </Typography>
            <button
              className="modal-close-button"
              onClick={onClose}
              aria-label="Close dialog"
            >
              <Icon name="close" size="small" />
            </button>
          </div>

          {/* Content */}
          <div className="modal-body">
            {children}
          </div>

          {/* Footer */}
          {(primaryButton || secondaryButton) && (
            <div className="modal-footer">
              <div className="modal-actions">
                {secondaryButton && (
                  <Button
                    variant="text"
                    onClick={secondaryButton.onClick}
                    disabled={secondaryButton.disabled}
                  >
                    {secondaryButton.label}
                  </Button>
                )}
                {primaryButton && (
                  <Button
                    variant="primary"
                    onClick={primaryButton.onClick}
                    disabled={primaryButton.disabled}
                  >
                    {primaryButton.label}
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>,
      document.body
    );
  };
  
  export default ModalDialog;